import React, { FC, useEffect, useRef, useState } from "react";
import { HiX } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { Button } from "@components";
import { HelpPopupProvider } from "../../contexts/HelpPopupContext";
import { modalSurface, mapOverlayHeader, modalFooter } from "@design-system";

export interface HelpPopupProps {
  content: string | React.ReactNode;
  onClose: () => void;
  title?: string;
}

export const HelpPopup: FC<HelpPopupProps> = ({ content, onClose, title }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("common");
  const [externalLink, setExternalLink] = useState<React.ReactNode | null>(
    null,
  );

  // Try to detect StoryblokHelpBlock
  const isStoryblokContent =
    typeof content !== "string" &&
    React.isValidElement(content) &&
    content.props &&
    "storySlug" in content.props &&
    "accessToken" in content.props;

  // Skip showing our own header for Storyblok content which has its own header
  const showHeader = !isStoryblokContent;
  const popupTitle = title || t("help.route_planner_help");

  useEffect(() => {
    // Handle click outside to close popup
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Handle escape key to close popup
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        ref={popupRef}
        className={`${modalSurface} w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4 relative`}
        role="dialog"
        aria-modal="true"
      >
        {showHeader ? (
          /* Original header with title for non-Storyblok content */
          <div className={`p-4 ${mapOverlayHeader} flex justify-between items-center`}>
            <h3 className="text-lg font-medium text-text">
              {popupTitle}
            </h3>
            <button
              className="text-gray-500 hover:text-gray-700 dark:text-muted dark:hover:text-gray-200"
              onClick={onClose}
              aria-label={t("help.close")}
            >
              <HiX className="h-5 w-5" />
            </button>
          </div>
        ) : (
          /* Just add a close button for Storyblok content */
          <button
            className="absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-700 dark:text-muted dark:hover:text-gray-200"
            onClick={onClose}
            aria-label={t("help.close")}
          >
            <HiX className="h-5 w-5" />
          </button>
        )}

        <div className="p-5 overflow-y-auto">
          <HelpPopupProvider onLinkChange={setExternalLink}>
            {typeof content === "string" ? (
              <div className="prose dark:prose-invert max-w-none">
                <p>{content}</p>
              </div>
            ) : (
              content
            )}
          </HelpPopupProvider>
        </div>

        <div className={`p-4 ${modalFooter} flex justify-between items-center`}>
          {/* External link from StoryblokHelpBlock */}
          <div>{externalLink}</div>

          <Button variant="primary" onClick={onClose}>
            {t("close")}
          </Button>
        </div>
      </div>
    </div>
  );
};
